<template>
  <div v-if="loading" class="notes-loading">
    <p>Loading notes...</p>
  </div>

  <div v-else-if="error" class="notes-error">
    <p>{{ error }}</p>
    <button class="retry-btn" @click="loadNotes">Retry</button>
  </div>

  <!-- Show notes section when there are notes OR when admin wants to add notes -->
  <div v-else-if="filteredNotes.length > 0 || showCreateForm || isAdmin" class="dashboard-notes">
    <div class="notes-header">
      <h3>
        <i class="fas fa-pushpin"></i>
        Pinned Notes
      </h3>
      <button v-if="isAdmin" class="add-note-btn" @click="showCreateForm = !showCreateForm">
        <i class="fas fa-plus"></i>
        Add Note
      </button>
    </div>

    <!-- Create Note Form -->
    <div v-if="showCreateForm" class="create-note-form">
      <div class="form-row">
        <input v-model="newNote.title" placeholder="Note title" class="form-input" />
        <select v-model="newNote.isLink" class="form-select">
          <option :value="false">Text Note</option>
          <option :value="true">Link Note</option>
        </select>
      </div>

      <div class="form-row">
        <input
          v-model="newNote.icon"
          :placeholder="
            newNote.isLink
              ? 'Icon (fa-link, https://example.com/icon.png)'
              : 'Icon (fa-info, https://example.com/icon.png)'
          "
          class="form-input"
        />
      </div>

      <textarea
        v-model="newNote.content"
        :placeholder="newNote.isLink ? 'Link URL (https://example.com)' : 'Note content'"
        class="form-textarea"
        rows="3"
      ></textarea>

      <div class="form-actions">
        <button :disabled="!canCreate" class="create-btn" @click="createNote">
          <i class="fas fa-check"></i>
          Create Note
        </button>
        <button class="cancel-btn" @click="cancelCreate">
          <i class="fas fa-times"></i>
          Cancel
        </button>
      </div>
    </div>

    <!-- Notes List -->
    <div v-if="filteredNotes.length > 0" class="notes-list">
      <div
        v-for="note in filteredNotes"
        :key="note.id"
        class="note-item"
        :class="{ 'note-link': note.isLink }"
      >
        <div class="note-content">
          <div class="note-icon">
            <!-- Image URL -->
            <img
              v-if="note.icon.startsWith('https://')"
              :src="note.icon"
              :alt="note.title"
              class="note-icon-image"
              @error="handleImageError"
            />
            <!-- Fallback for failed image -->
            <i
              v-if="note.icon.startsWith('https://')"
              class="fas fa-image note-icon-fallback"
              style="display: none"
            ></i>
            <!-- Font Awesome Icon -->
            <i v-else-if="note.icon.startsWith('fa-')" :class="`fas ${note.icon}`"></i>
            <!-- Fallback -->
            <i v-else class="fas fa-sticky-note"></i>
          </div>

          <div class="note-text">
            <!-- Link Note - Title is clickable, goes to URL in content -->
            <div v-if="note.isLink">
              <a
                :href="note.content"
                target="_blank"
                rel="noopener noreferrer"
                class="note-link-text"
              >
                {{ note.title }}
                <i class="fas fa-external-link-alt link-indicator"></i>
              </a>
            </div>
            <!-- Text Note -->
            <div v-else>
              <span class="note-title">{{ note.title }}</span>
              <pre v-if="note.content" class="note-description">{{ note.content }}</pre>
            </div>
          </div>
        </div>

        <!-- Note Actions (Admin only) -->
        <div v-if="isAdmin" class="note-actions">
          <button class="edit-btn" title="Edit Note" @click="editNote(note)">
            <i class="fas fa-edit"></i>
          </button>
          <button class="delete-btn" title="Delete Note" @click="deleteNote(note.id)">
            <i class="fas fa-trash"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Edit Note Modal -->
    <div v-if="editingNote" class="modal-overlay" @click="cancelEdit">
      <div class="modal-content" @click.stop>
        <h4>Edit Note</h4>

        <div class="form-row">
          <input v-model="editForm.title" placeholder="Note title" class="form-input" />
          <select v-model="editForm.isLink" class="form-select">
            <option :value="false">Text Note</option>
            <option :value="true">Link Note</option>
          </select>
        </div>

        <div class="form-row">
          <input
            v-model="editForm.icon"
            :placeholder="
              editForm.isLink
                ? 'Icon (fa-link, https://example.com/icon.png)'
                : 'Icon (fa-info, https://example.com/icon.png)'
            "
            class="form-input"
          />
        </div>

        <textarea
          v-model="editForm.content"
          :placeholder="editForm.isLink ? 'Link URL (https://example.com)' : 'Note content'"
          class="form-textarea"
          rows="3"
        ></textarea>

        <div class="form-actions">
          <button :disabled="!canUpdate" class="update-btn" @click="updateNote">
            <i class="fas fa-save"></i>
            Update
          </button>
          <button class="cancel-btn" @click="cancelEdit">
            <i class="fas fa-times"></i>
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { client, type AdminNote, parseErrorMessage, claims } from '../client.js';

interface Props {
  currentPage: string;
}

const props = defineProps<Props>();

const loading = ref(true);
const error = ref<string | null>(null);
const notes = ref<AdminNote[]>([]);
const showCreateForm = ref(false);
const editingNote = ref<AdminNote | null>(null);

const isAdmin = computed(() => claims.value?.isAdmin || false);

// Create form
const newNote = ref({
  icon: '',
  title: '',
  content: '',
  isLink: false,
  page: props.currentPage,
});

// Edit form
const editForm = ref({
  icon: '',
  title: '',
  content: '',
  isLink: false,
  page: '',
});

// Filter notes for current page
const filteredNotes = computed(() => {
  return notes.value.filter((note) => note.page === props.currentPage);
});

// Validation
const canCreate = computed(() => {
  const hasRequiredFields = newNote.value.title.trim() && newNote.value.icon.trim();
  // For link notes, content (URL) is required
  if (newNote.value.isLink) {
    return hasRequiredFields && newNote.value.content.trim();
  }
  return hasRequiredFields;
});

const canUpdate = computed(() => {
  const hasRequiredFields = editForm.value.title.trim() && editForm.value.icon.trim();
  // For link notes, content (URL) is required
  if (editForm.value.isLink) {
    return hasRequiredFields && editForm.value.content.trim();
  }
  return hasRequiredFields;
});

// Watch page changes to update form
watch(
  () => props.currentPage,
  (newPage) => {
    newNote.value.page = newPage;
    showCreateForm.value = false;
    editingNote.value = null;
  },
);

const loadNotes = async () => {
  if (!client.value) return;

  loading.value = true;
  error.value = null;

  try {
    notes.value = await client.value.getNotes();
  } catch (err: any) {
    error.value = parseErrorMessage(err);
    console.error('Failed to load notes:', err);
  } finally {
    loading.value = false;
  }
};

const createNote = async () => {
  if (!client.value || !canCreate.value) return;

  try {
    const createdNote = await client.value.createNote({
      icon: newNote.value.icon.trim(),
      title: newNote.value.title.trim(),
      content: newNote.value.content.trim(),
      isLink: newNote.value.isLink,
      page: newNote.value.page,
    });

    notes.value.unshift(createdNote);
    cancelCreate();
  } catch (err: any) {
    error.value = parseErrorMessage(err);
    console.error('Failed to create note:', err);
  }
};

const editNote = (note: AdminNote) => {
  editingNote.value = note;
  editForm.value = {
    icon: note.icon,
    title: note.title,
    content: note.content,
    isLink: note.isLink,
    page: note.page,
  };
};

const updateNote = async () => {
  if (!client.value || !editingNote.value || !canUpdate.value) return;

  try {
    const updatedNote = await client.value.updateNote(editingNote.value.id, {
      icon: editForm.value.icon.trim(),
      title: editForm.value.title.trim(),
      content: editForm.value.content.trim(),
      isLink: editForm.value.isLink,
      page: editForm.value.page,
    });

    const index = notes.value.findIndex((n) => n.id === updatedNote.id);
    if (index !== -1) {
      notes.value[index] = updatedNote;
    }

    cancelEdit();
  } catch (err: any) {
    error.value = parseErrorMessage(err);
    console.error('Failed to update note:', err);
  }
};

const deleteNote = async (id: string) => {
  if (!client.value || !confirm('Are you sure you want to delete this note?')) return;

  try {
    await client.value.deleteNote(id);
    notes.value = notes.value.filter((n) => n.id !== id);
  } catch (err: any) {
    error.value = parseErrorMessage(err);
    console.error('Failed to delete note:', err);
  }
};

const handleImageError = (event: Event) => {
  const img = event.target as HTMLImageElement;
  const fallbackIcon = img.nextElementSibling as HTMLElement;

  if (img && fallbackIcon) {
    img.style.display = 'none';
    fallbackIcon.style.display = 'flex';
  }
};

const cancelCreate = () => {
  showCreateForm.value = false;
  newNote.value = {
    icon: '',
    title: '',
    content: '',
    isLink: false,
    page: props.currentPage,
  };
};

const cancelEdit = () => {
  editingNote.value = null;
  editForm.value = {
    icon: '',
    title: '',
    content: '',
    isLink: false,
    page: '',
  };
};

onMounted(() => {
  loadNotes();
});
</script>

<style scoped>
.notes-loading,
.notes-error {
  text-align: center;
  padding: 1rem;
  color: #6b7280;
}

.notes-error p {
  color: #dc2626;
  margin-bottom: 1rem;
}

.retry-btn {
  padding: 0.5rem 1rem;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
}

.retry-btn:hover {
  background: #2563eb;
}

.dashboard-notes {
  background: white;
  border-radius: 0.75rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  margin-bottom: 2rem;
  overflow: hidden;
}

.notes-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.notes-header h3 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.notes-header h3 i {
  color: #6b7280;
}

.add-note-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  transition: background-color 0.2s;
}

.add-note-btn:hover {
  background: #2563eb;
}

.create-note-form {
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
}

.form-row {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
}

.form-input,
.form-select {
  flex: 1;
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  transition: border-color 0.2s;
}

.form-input:focus,
.form-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-textarea {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  resize: vertical;
  min-height: 80px;
  margin-bottom: 1rem;
  transition: border-color 0.2s;
}

.form-textarea:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-actions {
  display: flex;
  gap: 0.75rem;
}

.create-btn,
.update-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: #10b981;
  color: white;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  transition: background-color 0.2s;
}

.create-btn:hover:not(:disabled),
.update-btn:hover:not(:disabled) {
  background: #059669;
}

.create-btn:disabled,
.update-btn:disabled {
  background: #9ca3af;
  cursor: not-allowed;
}

.cancel-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: #6b7280;
  color: white;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  transition: background-color 0.2s;
}

.cancel-btn:hover {
  background: #4b5563;
}

.notes-list {
  padding: 0;
}

.note-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #f3f4f6;
  transition: background-color 0.2s;
}

.note-item:last-child {
  border-bottom: none;
}

.note-item:hover {
  background: #f9fafb;
}

.note-content {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  flex: 1;
}

.note-icon {
  flex-shrink: 0;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f3f4f6;
  border-radius: 0.5rem;
  color: #6b7280;
  position: relative;
  overflow: hidden;
}

.note-icon-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 0.5rem;
}

.note-icon-fallback {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.note-text {
  flex: 1;
  min-width: 0;
}

.note-link-text {
  color: #3b82f6;
  text-decoration: none;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.note-link-text:hover {
  text-decoration: underline;
}

.link-indicator {
  font-size: 0.75rem;
  opacity: 0.7;
}

.note-title {
  color: #1f2937;
  font-weight: 600;
  display: block;
  margin-bottom: 0.25rem;
}

.note-description {
  color: #6b7280;
  font-size: 0.875rem;
  line-height: 1.4;
  margin: 0;
  white-space: pre-wrap;
  word-wrap: break-word;
  font-family: inherit;
}

.note-actions {
  display: flex;
  gap: 0.5rem;
  opacity: 0;
  transition: opacity 0.2s;
}

.note-item:hover .note-actions {
  opacity: 1;
}

.edit-btn,
.delete-btn {
  padding: 0.25rem 0.5rem;
  border: none;
  border-radius: 0.25rem;
  cursor: pointer;
  font-size: 0.75rem;
  transition: background-color 0.2s;
}

.edit-btn {
  background: #f3f4f6;
  color: #6b7280;
}

.edit-btn:hover {
  background: #e5e7eb;
  color: #374151;
}

.delete-btn {
  background: #fef2f2;
  color: #dc2626;
}

.delete-btn:hover {
  background: #fee2e2;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.modal-content {
  background: white;
  border-radius: 0.75rem;
  padding: 2rem;
  width: 100%;
  max-width: 500px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.25);
}

.modal-content h4 {
  margin: 0 0 1.5rem 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
}

/* Responsive */
@media (max-width: 768px) {
  .notes-header {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }

  .form-row {
    flex-direction: column;
  }

  .form-actions {
    flex-direction: column;
  }

  .note-item {
    flex-direction: column;
    gap: 1rem;
  }

  .note-actions {
    opacity: 1;
    align-self: flex-end;
  }

  .modal-overlay {
    padding: 0.5rem;
  }

  .modal-content {
    padding: 1.5rem;
  }
}
</style>
